import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from './axiosBaseQuery';
import { mergeEnrichedFieldsIntoInput } from '@/lib/utils/workspace-utils/workspace.utils';
import type {
  WorkspaceUploadsListResponse,
  WorkspaceUploadStats,
  WorkspaceRowsPage,
  WorkspaceEnrichTriggerResponse,
  WorkspaceBulkEmailResponse,
  WorkspaceBulkPhoneResponse,
  WorkspaceUploadType,
} from '@/lib/types/workspace.types';

/**
 * Workspace API.
 *
 * Talks to the real Express middleware at `/api/workspace/*`, which resolves
 * the user from the session cookie and forwards to the standalone Workspace
 * service — no user id is sent from the browser.
 *
 * Creating anything — a workspace, or a sheet within one — goes through
 * `/api/uploads` instead (see uploadsApi.ts, type: 'workspace'), because
 * upstream has no create endpoint of either kind: `/workspace/uploadfile` is
 * the only thing that mints a userupload doc, and it mints the parent
 * workspace doc too whenever `workspace_id` is omitted. This slice covers
 * what's left: listing/polling/reading an already-dispatched batch,
 * deleting/downloading, and the three enrich triggers.
 */
export const workspaceApi = createApi({
  reducerPath: 'workspaceApi',
  baseQuery: axiosBaseQuery('/api/workspace'),
  tagTypes: ['WorkspaceUploads', 'WorkspaceRows'],
  refetchOnFocus: false,
  refetchOnReconnect: true,
  /**
   * Revalidate on mount rather than serving whatever is in cache.
   *
   * Everything in this slice is live server state that moves without any
   * mutation from this client: the matcher fills rows in and flips a sheet's
   * status/matchcount in the background, and the enrich jobs write columns.
   * Tag invalidation can't cover that — there's no local mutation to hang it
   * off — so a cached page could only ever be as fresh as the last time
   * something happened to be subscribed.
   *
   * `keepUnusedDataFor: 0` on getWorkspaceUploads was meant to force this,
   * but it only evicts once the subscriber count reaches *zero*, and this
   * endpoint has four subscribers (workspace home, the job list, the detail
   * page's sheet tabs, and the dashboard). Navigating back from a sheet to
   * the list kept at least one of them alive, so the entry was never dropped
   * and the list rendered pre-import, pre-completion data.
   */
  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    /** Every non-deleted workspace upload the signed-in user has kicked off. */
    getWorkspaceUploads: builder.query<WorkspaceUploadsListResponse, void>({
      query: () => '/uploads',
      providesTags: ['WorkspaceUploads'],
      keepUnusedDataFor: 0,
    }),

    /** Live status/counts for a single upload — what the progress screen polls. */
    getWorkspaceStats: builder.query<WorkspaceUploadStats, string>({
      query: (userUploadId) => `/stats/${userUploadId}`,
    }),

    /**
     * Paginated workspace_data rows (sorted by order_no) for a single upload.
     * Response rows are normalized on the way in — see
     * mergeEnrichedFieldsIntoInput — so every field (uploaded or enriched)
     * ends up under `input`, which is all the grid ever reads from.
     */
    getWorkspaceRows: builder.query<WorkspaceRowsPage, { userUploadId: string; pageNo?: number; pageSize?: number }>({
      query: ({ userUploadId, pageNo = 1, pageSize = 25 }) => ({
        url: `/rows/${userUploadId}`,
        params: { page_no: pageNo, page_size: pageSize },
      }),
      transformResponse: (response: WorkspaceRowsPage) => ({
        ...response,
        data: response.data.map(mergeEnrichedFieldsIntoInput),
      }),
      // Tagged per sheet so hiding columns can force this exact page to
      // refetch — the hide is applied upstream, so the rows that come back
      // are what the grid must render.
      providesTags: (_result, _error, { userUploadId }) => [{ type: 'WorkspaceRows', id: userUploadId }],
    }),

    /** Soft-deletes a workspace upload. */
    deleteWorkspaceUpload: builder.mutation<{ userupload_id: string; is_deleted: boolean }, string>({
      query: (userUploadId) => ({ url: `/delete/${userUploadId}`, method: 'GET' }),
      invalidatesTags: ['WorkspaceUploads'],
    }),

    /**
     * Persists the sheet's hidden columns. The array is the complete set
     * after the change, not a delta — un-hiding is the same call with a
     * shorter array.
     *
     * Invalidates this sheet's rows, so the pagination query refetches and
     * the grid re-renders from whatever the service now returns.
     */
    hideWorkspaceColumns: builder.mutation<unknown, { userUploadId: string; hiddenColumns: string[] }>({
      query: ({ userUploadId, hiddenColumns }) => ({
        url: `/hide-columns/${userUploadId}`,
        method: 'POST',
        body: { hidden_columns: hiddenColumns },
      }),
      invalidatesTags: (_result, _error, { userUploadId }) => [{ type: 'WorkspaceRows', id: userUploadId }],
    }),

    /** Backfills one field with each row's company website. */
    fetchDomain: builder.mutation<WorkspaceEnrichTriggerResponse, { userUploadId: string; domainName: string }>({
      query: ({ userUploadId, domainName }) => ({
        url: `/fetch-domain/${userUploadId}`,
        method: 'POST',
        body: { domainName },
      }),
    }),

    /** Backfills arbitrary target fields from the matched contact. */
    fetchContactDetail: builder.mutation<WorkspaceEnrichTriggerResponse, { userUploadId: string; mapping: Record<string, string> }>({
      query: ({ userUploadId, mapping }) => ({
        url: `/contact-detail/${userUploadId}`,
        method: 'POST',
        body: { mapping },
      }),
    }),

    /** Backfills arbitrary target fields from the matched company. */
    fetchCompanyDetail: builder.mutation<WorkspaceEnrichTriggerResponse, { userUploadId: string; mapping: Record<string, string> }>({
      query: ({ userUploadId, mapping }) => ({
        url: `/company-detail/${userUploadId}`,
        method: 'POST',
        body: { mapping },
      }),
    }),

    /**
     * Fires the bulk email job for a 1-indexed inclusive row range into one
     * column. A single Workspace-service trigger like the three above: the
     * backend fans out to the Workflow service per row itself (skipping
     * unmatched rows, deduping recent runs) and writes each result to the
     * row's `enriched_data[columnName]`, which is why getWorkspaceRows above
     * is all the grid needs to read them back.
     */
    sendBulkEmail: builder.mutation<
      WorkspaceBulkEmailResponse,
      { userUploadId: string; columnName: string; start: number; end: number }
    >({
      query: ({ userUploadId, columnName, start, end }) => ({
        url: `/send-bulk-email/${userUploadId}`,
        method: 'POST',
        body: { columnName, start, end },
      }),
    }),
    sendBulkPhone: builder.mutation<
      WorkspaceBulkPhoneResponse,
      { userUploadId: string; columnName: string; start: number; end: number }
    >({
      query: ({ userUploadId, columnName, start, end }) => ({
        url: `/sendBulkPhone/${userUploadId}`,
        method: 'POST',
        body: { requestId: userUploadId, columnName, start, end },
      }),
    }),

    lookalikeCompanies: builder.mutation<
      WorkspaceEnrichTriggerResponse,
      { userUploadId: string; columnName: string; noOfRecords: number }
    >({
      query: ({ userUploadId, columnName, noOfRecords }) => ({
        url: `/lookalikecompanies/${userUploadId}`,
        method: 'POST',
        body: { columnName, noOfRecords },
      }),
    }),

    lookalikeContacts: builder.mutation<
      WorkspaceEnrichTriggerResponse,
      { userUploadId: string; columnName: string; noOfRecords: number }
    >({
      query: ({ userUploadId, columnName, noOfRecords }) => ({
        url: `/lookalikecontacts/${userUploadId}`,
        method: 'POST',
        body: { columnName, noOfRecords },
      }),
    }),

    /**
     * Pushes this sheet's rows into the connected CRM. `mappingtype` is one
     * flat `column name -> CRM field name` dict — indicator matches and
     * column-mapping picks both fold into it before this is called. Not a
     * background job like the enrich triggers above: the response is
     * whatever the CRM service itself returned.
     */
    exportWorkspaceToCrm: builder.mutation<
      unknown,
      { userUploadId: string; source: string; mappingtype: Record<string, string>; segmentId?: string | null }
    >({
      query: ({ userUploadId, source, mappingtype, segmentId }) => ({
        url: `/export-crm/${userUploadId}`,
        method: 'POST',
        body: { source, mappingtype, segmentId: segmentId ?? null },
      }),
    }),

    /**
     * Pulls a CRM segment's records into a new workspace sheet. The upload is
     * minted server-side and enrichment matching starts in the background, so
     * the returned id is ready to open straight away on the progress screen.
     */
    importWorkspaceFromCrm: builder.mutation<
      { userupload_id: string; status: string },
      {
        segmentId: string;
        source: string;
        type: WorkspaceUploadType;
        filename: string;
        limit: number;
      }
    >({
      query: (body) => ({
        url: '/import-crm',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['WorkspaceUploads'],
    }),
  }),
});

export const {
  useGetWorkspaceUploadsQuery,
  useGetWorkspaceStatsQuery,
  useGetWorkspaceRowsQuery,
  useDeleteWorkspaceUploadMutation,
  useHideWorkspaceColumnsMutation,
  useFetchDomainMutation,
  useFetchContactDetailMutation,
  useFetchCompanyDetailMutation,
  useSendBulkEmailMutation,
  useSendBulkPhoneMutation,
  useLookalikeCompaniesMutation,
  useLookalikeContactsMutation,
  useExportWorkspaceToCrmMutation,
  useImportWorkspaceFromCrmMutation,
} = workspaceApi;

export default workspaceApi;
